<template>
  <BaseRender
    class="salary-field-mapping"
    showDefaultValue
    :loading="loading"
    :fieldTypeInfo="fieldTypeInfo"
    :fieldInfo="fieldInfo"
    :mappingList="mappingList"
    :jobCustomField="jobCustomField"
    @save="handleSave"
    @jobCustomFieldChange="handleJobCustomFieldChange"
  >
    <template #mappingTypeSwitchContainer>
      <div class="mapping-type-switch-container">
        <label class="mapping-type-label">{{ $t(`${i18nPrefix}.label.mappingType`) }}</label>
        <el-switch
          v-model="isCustomMapping"
          class="mapping-type-value"
          active-color="#0096cc"
          inactive-color="#13ce66"
          :active-text="$t(`${i18nPrefix}.label.customMapping`)"
          :inactive-text="$t(`${i18nPrefix}.label.jobCustomField`)"
          >
        </el-switch>
      </div>
    </template>
    <template v-if="!isCustomMapping" #jobCustomFieldMappingType>
      <Salary
        showSalaryType
        ref="JobCustomFieldMappingType"
        :hiddenSalaryMonth="jobCustomFieldMappingVal.salaryType.value !== yearlySalary"
        :jobCustomFieldMappingVal="jobCustomFieldMappingVal"
        :salaryTypeList="salaryTypeList"
        :salaryMonthList="salaryMonthList"
        :jobCustomFieldList="jobCustomFieldList"
      >
      </Salary>
    </template>
    <template #defaultValue="{ refName }">
      <div class="salary-default-container">
        <el-select
          v-model="defaultSalaryType"
          class="salary-type"
          :ref="`${refName}_type`"
          @focus="clearError(`${refName}_type`)"
        >
          <el-option
            v-for="(item, index) in salaryTypeList"
            :key="index"
            :value="item.id"
            :label="getI18nPropertyValue(item, 'cn_name', 'name')"
          ></el-option>
        </el-select>
        <el-input
          v-model="defaultSalaryMin"
          clearable
          class="salary-min"
          :placeholder="$t(`admin.rpa.placeholder.salary.min`)"
          :ref="`${refName}_minSalary`"
          @focus="clearError(`${refName}_minSalary`)"
        >
        </el-input>
        <span class="split-icon">-</span>
        <el-input
          v-model="defaultSalaryMax"
          clearable
          class="salary-max"
          :placeholder="$t(`admin.rpa.placeholder.salary.max`)"
          :ref="`${refName}_maxSalary`"
          @focus="clearError(`${refName}_maxSalary`)"
        >
        </el-input>
        <span class="split-icon" v-if="isShowDefaultSalaryMonth">x</span>
        <el-select
          v-if="isShowDefaultSalaryMonth"
          v-model="defaultSalaryMonth"
          class="salary-month"
          :ref="`${refName}_month`"
          @focus="clearError(`${refName}_month`)"
        >
          <el-option
            v-for="(item, index) in salaryMonthList"
            :key="index"
            :value="item.id"
            :label="getI18nPropertyValue(item, 'cn_name', 'name')"
          ></el-option>
        </el-select>
      </div>
    </template>
    <template #selfFieldRender="{data}">
      <span class="self-field-name">{{ getI18nPropertyValue(data, 'cn_name', 'name') }}</span>
    </template>
    <template #otherFieldRender="{data, refName}">
      <div class="salary-container">
        <el-select
          v-model="data.type"
          class="salary-type"
          :ref="`${refName}_type`"
          @focus="clearError(`${refName}_type`)"
        >
          <el-option
            v-for="(item, index) in salaryTypeList"
            :key="index"
            :value="item.id"
            :label="getI18nPropertyValue(item, 'cn_name', 'name')"
          ></el-option>
        </el-select>
        <el-input
          v-model="data.min"
          clearable
          class="salary-min"
          :placeholder="$t(`admin.rpa.placeholder.salary.min`)"
          :ref="`${refName}_minSalary`"
          @focus="clearError(`${refName}_minSalary`)"
        >
        </el-input>
        <span class="split-icon">-</span>
        <el-input
          v-model="data.max"
          clearable
          class="salary-max"
          :placeholder="$t(`admin.rpa.placeholder.salary.max`)"
          :ref="`${refName}_maxSalary`"
          @focus="clearError(`${refName}_maxSalary`)"
        >
        </el-input>
        <span class="split-icon" v-if="data.type === yearlySalary">x</span>
        <el-select
          v-if="data.type === yearlySalary"
          class="salary-month"
          v-model="data.month"
          :ref="`${refName}_month`"
          @focus="clearError(`${refName}_month`)"
        >
          <el-option
            v-for="(item, index) in salaryMonthList"
            :key="index"
            :value="item.id"
            :label="getI18nPropertyValue(item, 'cn_name', 'name')"
          ></el-option>
        </el-select>
      </div>
    </template>
  </BaseRender>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import channelId from '../channelId'
import mixin from '../mixin'
import Salary from '../common/salary.vue'

const SALARY_PAYMENT_TYPE_OPTION = {
  DEFAULT_VALUE: 1
}

const SALARY_TYPE_OPTION = {
  FIXED_VALUE: 1,
  CUSTOM_FIELDS: 2
}

const SALARY_MONTHS_OPTION = {
  DEFAULT_VALUE: 1
}

class OtherSalary {
  constructor (minSalary, maxSalary, month, type, key = 'value') {
    this.minSalary = minSalary
    this.maxSalary = maxSalary
    this.salaryMonth = month
    this.salaryType = type
    this.key = key
    this.defaultSalaryMonth = this.twelveSalary
    this.defaultSalaryType = this.yearlySalary
  }

  get max () {
    return this.maxSalary[this.key]
  }

  set max (val) {
    this.maxSalary[this.key] = val
  }

  get min () {
    return this.minSalary[this.key]
  }

  set min (val) {
    this.minSalary[this.key] = val
  }

  get month () {
    return this.salaryMonth[this.key] || this.defaultSalaryMonth
  }

  set month (val) {
    this.salaryMonth[this.key] = val
  }

  get type () {
    return this.salaryType[this.key] || this.defaultSalaryType
  }

  set type (val) {
    this.salaryType[this.key] = val
  }
}

function createEmptyOtherSalary () {
  return new OtherSalary({ value: null }, { value: null }, { value: null }, { value: null })
}

export default {
  components: {
    Salary
  },
  name: `channel-${channelId.job51}-fieldType-salary`,
  mixins: [mixin],
  data () {
    return {
      getI18nPropertyValue,
      monthSalaryId: null,
      salaryTypeList: null,
      yearlySalary: null,
      twelveSalary: null,
      mappingList: [],
      jobSalaryList: [],
      salaryMonthList: [],
      jobCustomField: {
        show: true,
        fieldId: null,
        field: null,
        fieldList: []
      },
      jobCustomFieldList: [],
      isCustomMapping: false,
      jobCustomFieldMappingVal: {
        salaryType: {
          type: SALARY_PAYMENT_TYPE_OPTION.DEFAULT_VALUE,
          value: null
        },
        minSalary: {
          type: SALARY_TYPE_OPTION.FIXED_VALUE,
          value: null,
          customFiledValue: null
        },
        maxSalary: {
          type: SALARY_TYPE_OPTION.FIXED_VALUE,
          value: null,
          customFiledValue: null
        },
        salaryMonth: {
          type: SALARY_MONTHS_OPTION.DEFAULT_VALUE,
          value: null
        }
      }
    }
  },
  computed: {
    salaryTypeField () {
      return this.fieldInfo.salary_type
    },
    salaryValueField () {
      return this.fieldInfo.salary_value
    },
    minSalaryField () {
      return this.fieldInfo.salary_min
    },
    maxSalaryField () {
      return this.fieldInfo.salary_max
    },
    salaryMonthField () {
      return this.fieldInfo.salary_month
    },
    salaryIsCustomField () {
      return this.fieldInfo.is_custom_salary
    },
    isShowDefaultSalaryMonth () {
      return this.defaultSalaryType === this.yearlySalary
    },
    defaultSalaryType: {
      get () {
        return this.salaryTypeField.default || this.yearlySalary
      },
      set (value) {
        this.salaryTypeField.default = value
      }
    },
    defaultSalaryMin: {
      get () {
        return this.minSalaryField.default
      },
      set (value) {
        this.minSalaryField.default = value
      }
    },
    defaultSalaryMax: {
      get () {
        return this.maxSalaryField.default
      },
      set (value) {
        this.maxSalaryField.default = value
      }
    },
    defaultSalaryMonth: {
      get () {
        return this.salaryMonthField.default
      },
      set (value) {
        this.salaryMonthField.default = value
      }
    }
  },
  created () {
    this.refreshPageData()
  },
  methods: {
    ...mapActions('job', ['getJobSalaryList']),
    ...mapActions('jobPosting', ['getPostingFieldOptions']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    getDataFromApi () {
      return Promise.all([
        this.getJobSalaryList().then(
          resp => {
            if (resp) {
              this.jobSalaryList = resp
            }
          }
        ),
        this.getPostingFieldOptions({ fieldTypeId: this.fieldTypeInfo.id, params: { field_type: '51job_month_salary' } }).then(
          resp => {
            if (resp) {
              this.salaryMonthList = _.filter(resp.options, item => /\d/.test(item.value))
              this.salaryTypeList = _.filter(resp.options, item => !/\d/.test(item.value))
              this.twelveSalary = _.find(resp.options, item => item.value === '12薪').id
              this.yearlySalary = _.find(resp.options, item => item.value === '年薪').id
            }
          }
        ),
        this.searchJobCustomField({ status: 1, type: 'SELECT,SINGLE,TEXT,TEXTAREA', need_options: true }).then(
          resp => {
            if (resp) {
              const fieldList = []
              _.forEach(resp, field => {
                if (field.type === 'SELECT') {
                  fieldList.push(field)
                }
                addI18nProperty(field, 'i18nName', 'cn_name', 'name')
              })
              this.jobCustomFieldList = resp
              this.jobCustomField.fieldList = fieldList
              const fieldId = this.jobCustomField.fieldId
              if (fieldId) {
                this.jobCustomField.field = _.find(fieldList, { id: fieldId })
              }
            }
          }
        )
      ])
    },
    async refreshPageData (noNeedApiData) {
      this.loading = true
      try {
        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        const salaryTypeField = this.salaryTypeField
        if (!noNeedApiData) {
          await this.getDataFromApi()
        }
        if (salaryMonthField.default) {
          salaryMonthField.default = parseInt(salaryMonthField.default)
        }
        if (salaryTypeField.default) {
          salaryTypeField.default = parseInt(salaryTypeField.default)
        }
        if (!minSalaryField.data_source || minSalaryField.data_source === 'CUSTOM_DEFAULT') {
          this.isCustomMapping = false
          this.jobCustomFieldMappingVal = {
            salaryType: {
              type: SALARY_PAYMENT_TYPE_OPTION.DEFAULT_VALUE,
              value: salaryTypeField.default
            },
            minSalary: {
              type: minSalaryField.data_key_path === 'FIXED' ? SALARY_TYPE_OPTION.FIXED_VALUE : SALARY_TYPE_OPTION.CUSTOM_FIELDS,
              value: minSalaryField.data_key_path === 'FIXED' ? minSalaryField.default : null,
              customFiledValue: minSalaryField.data_key_path === 'FIXED' ? null : parseInt(minSalaryField.default) || null
            },
            maxSalary: {
              type: maxSalaryField.data_key_path === 'FIXED' ? SALARY_TYPE_OPTION.FIXED_VALUE : SALARY_TYPE_OPTION.CUSTOM_FIELDS,
              value: maxSalaryField.data_key_path === 'FIXED' ? maxSalaryField.default : null,
              customFiledValue: maxSalaryField.data_key_path === 'FIXED' ? null : parseInt(maxSalaryField.default) || null
            },
            salaryMonth: {
              type: SALARY_MONTHS_OPTION.DEFAULT_VALUE,
              value: salaryMonthField.default
            }
          }
          minSalaryField.default = null
          maxSalaryField.default = null
          salaryTypeField.default = null
          salaryMonthField.default = null
          minSalaryField.data_map_list = []
          maxSalaryField.data_map_list = []
          salaryMonthField.data_map_list = []
          salaryTypeField.data_map_list = []
        } else {
          this.isCustomMapping = true
        }
        let isJobCustomField = false
        if (minSalaryField.data_source === 'CUSTOM_FIELD') {
          if (minSalaryField.data_key_path) {
            const fieldId = parseInt(minSalaryField.data_key_path)
            this.jobCustomField.fieldId = fieldId
            if (!this.jobCustomField.field) {
              this.jobCustomField.field = _.find(this.jobCustomField.fieldList, { id: fieldId })
            }
          }
          isJobCustomField = true
        }
        const otherFieldMap = {}
        const minSalaryDataMapList = minSalaryField.data_map_list
        const maxSalaryDataMapList = maxSalaryField.data_map_list
        const salaryMonthDataMapList = salaryMonthField.data_map_list
        const salaryTypeDataMapList = salaryTypeField.data_map_list
        _.forEach(minSalaryDataMapList, (minSalaryDataMap, index) => {
          const maxSalaryDataMap = maxSalaryDataMapList[index]
          const salaryMonthDataMap = salaryMonthDataMapList[index]
          const salaryTypeDataMap = salaryTypeDataMapList[index]

          this.setOtherField('minSalary', minSalaryDataMap, otherFieldMap)
          this.setOtherField('maxSalary', maxSalaryDataMap, otherFieldMap)
          this.setOtherField('salaryMonth', salaryMonthDataMap, otherFieldMap)
          this.setOtherField('salaryType', salaryTypeDataMap, otherFieldMap)
        })

        const selfFieldList = isJobCustomField ? this.jobCustomField.field.options : this.jobSalaryList
        this.mappingList = _.map(selfFieldList, (item, index) => {
          let otherField = null
          if (otherFieldMap.hasOwnProperty(item.id)) {
            otherField = otherFieldMap[item.id]
          } else {
            otherField = createEmptyOtherSalary()
          }
          return {
            id: index + 1,
            self: item,
            other: otherField
          }
        })
      } finally {
        this.loading = false
      }
    },
    handleJobCustomFieldChange (fieldId) {
      const selfFieldList = fieldId ? this.jobCustomField.field.options : this.jobSalaryList
      this.mappingList = _.map(selfFieldList, (item, index) => {
        return {
          id: index + 1,
          self: item,
          other: createEmptyOtherSalary()
        }
      })
    },
    setOtherField (name, dataMap, otherFieldMap) {
      dataMap.key = parseInt(dataMap.key) || null
      dataMap.value = parseInt(dataMap.value) || null
      const dataKey = dataMap.key
      let otherField = null
      if (otherFieldMap.hasOwnProperty(dataKey)) {
        otherField = otherFieldMap[dataKey]
      } else {
        otherFieldMap[dataKey] = otherField = new OtherSalary()
      }
      otherField[name] = dataMap
    },
    handleSave () {
      if (this.isCustomMapping) {
        const errorNames = []
        if (!this.defaultSalaryMin) {
          errorNames.push('defaultValue_minSalary')
        }
        if (!this.defaultSalaryMax) {
          errorNames.push('defaultValue_maxSalary')
        }

        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        const salaryTypeField = this.salaryTypeField
        const isJobCustomField = Boolean(this.jobCustomField.fieldId)
        const dataMapList = []
        let hasOldData = false
        _.forEach(this.mappingList, item => {
          const otherField = item.other
          const selfField = item.self
          if (!otherField.minSalary.value && otherField.maxSalary.value) {
            errorNames.push(`other_${item.id}_minSalary`)
          }
          if (otherField.minSalary.value && !otherField.maxSalary.value) {
            errorNames.push(`other_${item.id}_maxSalary`)
          }
          if (otherField.minSalary.id) {
            hasOldData = true
            return
          }
          dataMapList.push({
            key: selfField.id,
            minSalary: otherField.minSalary.value,
            maxSalary: otherField.maxSalary.value,
            salaryMonth: otherField.salaryMonth.value,
            salaryType: otherField.salaryType.value
          })
        })
        if (errorNames.length > 0) {
          this.setError(errorNames)
          return
        }
        if (!hasOldData) {
          minSalaryField.data_map_list = []
          maxSalaryField.data_map_list = []
          salaryMonthField.data_map_list = []
          salaryTypeField.data_map_list = []
        }
        if (isJobCustomField) {
          minSalaryField.data_source = 'CUSTOM_FIELD'
          minSalaryField.data_key_path = `${this.jobCustomField.fieldId}`
          maxSalaryField.data_source = 'CUSTOM_FIELD'
          maxSalaryField.data_key_path = `${this.jobCustomField.fieldId}`
          salaryMonthField.data_source = 'CUSTOM_FIELD'
          salaryMonthField.data_key_path = `${this.jobCustomField.fieldId}`
          salaryTypeField.data_source = 'CUSTOM_FIELD'
          salaryTypeField.data_key_path = `${this.jobCustomField.fieldId}`
        } else {
          minSalaryField.data_source = 'SALARY'
          minSalaryField.data_key_path = 'id'
          maxSalaryField.data_source = 'SALARY'
          maxSalaryField.data_key_path = 'id'
          salaryMonthField.data_source = 'SALARY'
          salaryMonthField.data_key_path = 'id'
          salaryTypeField.data_source = 'SALARY'
          salaryTypeField.data_key_path = 'id'
        }
        _.forEach(dataMapList, item => {
          if (!item.minSalary) {
            return
          }
          minSalaryField.data_map_list.push({
            key: item.key,
            value: item.minSalary
          })
          maxSalaryField.data_map_list.push({
            key: item.key,
            value: item.maxSalary
          })
          salaryMonthField.data_map_list.push({
            key: item.key,
            value: item.salaryMonth
          })
          salaryTypeField.data_map_list.push({
            key: item.key,
            value: item.salaryType
          })
        })
      } else {
        const isValid = this.$refs.JobCustomFieldMappingType.validate()
        if (!isValid) {
          return
        }
        const mapping = this.jobCustomFieldMappingVal
        const minSalaryField = this.minSalaryField
        const maxSalaryField = this.maxSalaryField
        const salaryMonthField = this.salaryMonthField
        const salaryTypeField = this.salaryTypeField
        minSalaryField.data_source = 'CUSTOM_DEFAULT'
        minSalaryField.data_key_path = mapping.minSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? 'FIXED' : 'JOB_CUSTOM_FIELD'
        minSalaryField.default = mapping.minSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? mapping.minSalary.value : mapping.minSalary.customFiledValue
        minSalaryField.data_map_list = []

        maxSalaryField.data_source = 'CUSTOM_DEFAULT'
        maxSalaryField.data_key_path = mapping.maxSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? 'FIXED' : 'JOB_CUSTOM_FIELD'
        maxSalaryField.default = mapping.maxSalary.type === SALARY_TYPE_OPTION.FIXED_VALUE ? mapping.maxSalary.value : mapping.maxSalary.customFiledValue
        maxSalaryField.data_map_list = []

        salaryMonthField.data_source = 'CUSTOM_DEFAULT'
        salaryMonthField.data_key_path = 'ORIGIN'
        salaryMonthField.default = mapping.salaryMonth.value
        salaryMonthField.data_map_list = []

        salaryTypeField.data_source = 'CUSTOM_DEFAULT'
        salaryTypeField.data_key_path = 'ORIGIN'
        salaryTypeField.default = mapping.salaryType.value
        salaryTypeField.data_map_list = []
      }
      this.$emit('save')
    }
  }
}
</script>

<style lang="scss" scoped>
.salary-field-mapping {
  ::v-deep {
    .salary-default-container {
      display: flex;
      align-items: center;

      .salary-type {
        width: 22%;
        margin-right: auto;
      }

      .salary-min.el-input {
        width: 22%;
      }

      .salary-max.el-input {
        width: 22%;
      }

      .salary-month {
        width: 22%;
      }

      .el-input__icon {
        line-height: 30px;
      }

      .split-icon {
        padding: 0 8px;
      }
    }

    .el-table {
      .el-table__body {

        .other-field {
          .cell {
            padding-left: 5px;
            padding-right: 5px;
          }

          .salary-container {
            display: flex;
            align-items: center;

            .salary-type {
              width: 20%;
              margin-right: 10px;
            }
            .salary-min {
              width: 20%;
            }
            .salary-max {
              width: 20%;
            }
            .salary-month {
              width: 20%;
            }

            .el-input__suffix {
              right: 0;
            }

            .split-icon {
              padding: 0 10px;
            }
          }

          .salary-select {
            .el-input__inner {
              padding-left: 5px;
            }
          }
        }
      }
    }

    .salary-select {
      width: 100%;
    }
  }

}
</style>
